import _createClient from "openapi-fetch";

import {
  getActionGives,
  getActionInfo,
  getActionInfoAndNeeds,
  getActionSample,
  getControlledApps,
} from "./actions";
import { getNeeds } from "./actions";
import {
  getAuthentications,
  getAuthenticationsByIds,
  testAuth,
} from "./authentications";
import { getChoices, getChoicesPaged } from "./choices";
import { getAppInfo, getImplementation } from "./implementations";
import { getInvocationResults, invokeAction } from "./invoke";
import type { paths } from "./monolith-type-overrides";
import { searchActions, searchApps } from "./search";
import { ZapierApiClient, ZapierFacadeError } from "./types";

export class AgentsZapierFacade {
  /** Generated API client from OpenAPI types */
  public readonly api: ZapierApiClient;

  public readonly cache: Record<string, any> = {};

  /** Function to handle errors */
  private readonly onError?: (error: ZapierFacadeError) => Promise<void>;

  constructor(
    args: Parameters<typeof _createClient<paths>>[0] & {
      onError?: (error: ZapierFacadeError) => Promise<void>;
    },
  ) {
    const { onError, ...clientArgs } = args;
    this.api = _createClient<paths>(clientArgs);

    if (onError) {
      this.onError = onError;
    }
  }

  /**
   * Calls the given function and sends any ZapierFacadeErrors to the onError function.
   *
   * Will still throw the error.
   */
  private callWrapped = async <T>(
    func: (...args: any[]) => Promise<T>,
    ...args: any[]
  ) => {
    try {
      return await func(...args);
    } catch (error) {
      if (error instanceof ZapierFacadeError && this.onError) {
        await this.onError(error);
      }

      throw error;
    }
  };

  /** Search for apps */
  async searchApps(params: Parameters<typeof searchApps>[1]) {
    return this.callWrapped(searchApps, this, params);
  }

  /** Get the actions for a specific app and search within them. */
  async searchActions(params: Parameters<typeof searchActions>[1]) {
    return this.callWrapped(searchActions, this, params);
  }

  /** Get list of authentications for an app. */
  async getAuthentications(params: Parameters<typeof getAuthentications>[1]) {
    return this.callWrapped(getAuthentications, this, params);
  }

  /** Look up specific authentications by ID. */
  async getAuthenticationsByIds(
    params: Parameters<typeof getAuthenticationsByIds>[1],
  ) {
    return this.callWrapped(getAuthenticationsByIds, this, params);
  }

  /** Test an authentication */
  async testAuth(params: Parameters<typeof testAuth>[1]) {
    return this.callWrapped(testAuth, this, params);
  }

  /** Get info about a specific action */
  async getActionInfo(params: Parameters<typeof getActionInfo>[1]) {
    return this.callWrapped(getActionInfo, this, params);
  }

  /** Get info about a specific app */
  async getAppInfo(params: Parameters<typeof getAppInfo>[1]) {
    return this.callWrapped(getAppInfo, this, params);
  }

  /** Get info about a specific action and its needs */
  async getActionInfoAndNeeds(
    params: Parameters<typeof getActionInfoAndNeeds>[1],
  ) {
    return this.callWrapped(getActionInfoAndNeeds, this, params);
  }

  /** Get live needs for a specific action */
  async getNeeds(params: Parameters<typeof getNeeds>[1]) {
    return this.callWrapped(getNeeds, this, params);
  }

  /** Get the gives for a specific action */
  async getActionGives(params: Parameters<typeof getActionGives>[1]) {
    return this.callWrapped(getActionGives, this, params);
  }

  /** Get a sample of what the action would return */
  async getActionSample(params: Parameters<typeof getActionSample>[1]) {
    return this.callWrapped(getActionSample, this, params);
  }

  /** Get list of choices for an app. */
  async getChoices(params: Parameters<typeof getChoices>[1]) {
    return this.callWrapped(getChoices, this, params);
  }

  /** Get list of choices for an app. */
  async getChoicesPaged(params: Parameters<typeof getChoicesPaged>[1]) {
    return getChoicesPaged(this, params);
  }

  async getImplementation(params: Parameters<typeof getImplementation>[1]) {
    return getImplementation(this, params);
  }

  /** Invoke an action. */
  async invokeAction(params: Parameters<typeof invokeAction>[1]) {
    return this.callWrapped(invokeAction, this, params);
  }

  /** Get the results of an invocation. */
  async getInvocationResults(
    params: Parameters<typeof getInvocationResults>[1],
  ) {
    return this.callWrapped(getInvocationResults, this, params);
  }

  /** Get the controlled apps for an account. */
  async getControlledApps(params: Parameters<typeof getControlledApps>[1]) {
    return getControlledApps(this, params);
  }
}

export * from "./types";
export { FILTERED_APPS, FILTERED_ACTIONS } from "./constants";

export { getVersionlessApi, isVersionlessSelectedApiControlled } from "./util";
